import { FunctionComponent } from 'preact';
import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import clsx from 'clsx';
import { TopMenu } from '@accedo/ctv-ui-menu';
import type { MenuItem } from '@accedo/ctv-ui-menu';
import { logger } from '@accedo/logger';
import { usePageNavigation } from '@accedo/page-navigation';
import MenuLogo from '##/assets/images/NZ_On_Screen_logo.png';
import { MenuItem as MenuItemModel } from '##/dataModels/menuItem';
import { MenuComponentProps } from '##/components/MenuWrapper/types';
import { useMenu } from '##/context/MenuContext';
import { getIconByViaLabel } from '##/components/MenuWrapper/utils';
import { useAuthContext } from '##/context/AuthContext';
import { addPublicPath, removePublicPath } from '##/services/routing';
import styles from './menu.module.scss';

// Log Prefix
const LP = '[MenuWrapper]';
interface MenuItemsResponse {
  primaryItems: MenuItem[];
  secondaryItems: MenuItem[];
}

const getMenuItems = (items: MenuItemModel[], isAuthenticated: boolean): MenuItemsResponse => {
  const filterFn = (item: MenuItemModel): boolean =>
    isAuthenticated ? !item.hiddenWhenAuth : !item.requireAuthentication;

  const primaryItems = items.filter((item) => item.type !== 'secondary').filter(filterFn) as MenuItem[];

  const secondaryItems = items.filter((item) => item.type === 'secondary').filter(filterFn) as MenuItem[];

  return { primaryItems, secondaryItems };
};

const MenuWrapper: FunctionComponent<MenuComponentProps> = ({
  id,
  index,
  items,
  logo,
  renderMode = 'high',
  size = 'medium',
}) => {
  const [activeItemId, setActiveItemId] = useState<string>();
  const { navigateTo, path } = usePageNavigation();
  const { isVisible: isMenuVisible } = useMenu();
  const { isAuthenticated } = useAuthContext();

  const menuItems: MenuItemsResponse = useMemo(() => {
    const filteredItems = items.map((item) => {
      const shouldDisplayIcon = !!item.icon;
      const isPrimary = item.type !== 'secondary';
      return {
        ...item,
        label: isPrimary || !shouldDisplayIcon ? item.label : '',
        leadingIcon: shouldDisplayIcon ? getIconByViaLabel(item.icon as string) : '',
      };
    });

    return getMenuItems(filteredItems, isAuthenticated);
  }, [items, isAuthenticated]);

  useEffect(() => {
    const { primaryItems, secondaryItems } = menuItems;
    const mergedMenuItems = [...primaryItems, ...secondaryItems];
    const cleanPath = removePublicPath(path);
    const activeItem = mergedMenuItems.find((item) => item?.route === cleanPath);
    if (activeItem && activeItem.id) {
      setActiveItemId(activeItem.id);
    } else if (primaryItems && primaryItems.length > 0 && primaryItems[0].id) {
      setActiveItemId(primaryItems[0].id);
    }
  }, [path, menuItems]);

  const onMenuItemClick = useCallback(
    (item: MenuItem, sectionId: string): void => {
      logger.debug(`${LP} onMenuItemClick item.route ${item.route} `);
      if (item.route) {
        navigateTo(addPublicPath(item.route), {
          navigateFromState: {
            data: {
              menuItemId: item.id,
              menuSectionId: sectionId,
            },
          },
        });
      }
    },
    [navigateTo],
  );

  if (!isMenuVisible) {
    logger.debug(`${LP} Menu is not visible`);
    return null;
  }

  return (
    <TopMenu
      activeId={activeItemId}
      id={`${id}-top-menu`}
      key={`${id}-top-menu`}
      index={index}
      logo={{
        position: logo?.position ?? 'leading',
        baseLogoSrc: logo?.baseLogoSrc ?? MenuLogo,
      }}
      onItemClick={onMenuItemClick}
      primarySection={{
        items: menuItems.primaryItems,
        styling: {
          items: {
            className: styles.menuItem,
          },
        },
      }}
      renderMode={renderMode}
      secondarySection={{
        items: menuItems.secondaryItems,
        styling: {
          items: {
            className: styles.menuItem,
          },
        },
      }}
      size={size}
      styling={{
        className: clsx(styles.menuContainer, !isMenuVisible && styles.menuHidden),
      }}
    />
  );
};

export default MenuWrapper;
